(() => {
  const PERFUMES_PER_PROMO = 3;
  const MAX_PROMOS = 10;

  const normalize = (value) =>
    String(value || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();

  class PromoPicker {
    constructor(root) {
      this.root = root;
      this.catalog = this.readCatalog();
      this.promos = [{ selection: [] }];
      this.activePromo = 0;
      this.filter = "todos";
      this.query = "";
      this.unitPrice = Number(root.dataset.unitPriceCents || 5499);
      this.bulkPrice = Number(root.dataset.bulkPriceCents || 5000);
      this.bulkMinimum = Number(root.dataset.bulkMinimum || 3);
      this.grid = root.querySelector("[data-product-grid]");
      this.search = root.querySelector("[data-search]");
      this.empty = root.querySelector("[data-empty]");
      this.resultCount = root.querySelector("[data-result-count]");
      this.slots = root.querySelector("[data-selection-slots]");
      this.dock = root.querySelector("[data-selection-dock]");
      this.count = root.querySelector("[data-selection-count]");
      this.title = root.querySelector("[data-selection-title]");
      this.selectorTitle = root.querySelector("[data-selector-title]");
      this.progress = root.querySelector("[data-progress]");
      this.submit = root.querySelector("[data-submit]");
      this.submitLabel = root.querySelector("[data-submit-label]");
      this.addPromo = root.querySelector("[data-add-promo]");
      this.orderSummary = root.querySelector("[data-order-summary]");
      this.comboTabs = root.querySelector("[data-promo-tabs]");
      this.message = root.querySelector("[data-message]");
      this.bindEvents();
      this.observeSelector();
      this.render();
    }

    get currentPromo() {
      return this.promos[this.activePromo];
    }

    readCatalog() {
      const node = this.root.querySelector("[data-catalog]");
      if (!node) return [];
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error("No se pudo leer el catálogo promocional", error);
        return [];
      }
    }

    bindEvents() {
      this.search?.addEventListener("input", (event) => {
        this.query = normalize(event.target.value);
        this.renderGrid();
      });

      this.root.querySelectorAll("[data-filter]").forEach((button) => {
        button.addEventListener("click", () => {
          this.filter = button.dataset.filter;
          this.root.querySelectorAll("[data-filter]").forEach((item) => {
            const active = item === button;
            item.classList.toggle("is-active", active);
            item.setAttribute("aria-pressed", String(active));
          });
          this.renderGrid();
        });
      });

      this.addPromo?.addEventListener("click", () => this.addPromotion());
      this.submit?.addEventListener("click", () => this.submitOrder());

      document.addEventListener("shopify:section:load", (event) => {
        const sectionRoot = event.target.querySelector?.("[data-promo-picker]");
        if (sectionRoot && !sectionRoot.dataset.initialized) new PromoPicker(sectionRoot);
      });
    }

    observeSelector() {
      const selector = this.root.querySelector("#selector");
      if (!("IntersectionObserver" in window) || !selector || !this.dock) {
        this.dock?.classList.add("is-visible");
        return;
      }
      this.selectorObserver = new IntersectionObserver(
        (entries) => {
          const selectorVisible = entries.some((entry) => entry.isIntersecting);
          const hasSelection = this.promos.some((promo) => promo.selection.length > 0);
          this.dock.classList.toggle("is-visible", selectorVisible || hasSelection);
        },
        { rootMargin: "0px 0px -20% 0px", threshold: 0.01 }
      );
      this.selectorObserver.observe(selector);
    }

    filteredCatalog() {
      return this.catalog.filter((product) => {
        const matchesFilter = this.filter === "todos" || product.category === this.filter;
        const matchesQuery = !this.query || normalize(product.name).includes(this.query);
        return matchesFilter && matchesQuery;
      });
    }

    render() {
      this.root.dataset.initialized = "true";
      this.renderPromoTabs();
      this.renderGrid();
      this.renderSelection();
    }

    renderPromoTabs() {
      if (!this.comboTabs) return;
      const nodes = this.promos.map((promo, index) => {
        const wrap = document.createElement("span");
        wrap.className = `promo-tab-wrap${index === this.activePromo ? " is-active" : ""}`;

        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = "promo-tab";
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", String(index === this.activePromo));
        tab.textContent = `Promoción ${index + 1} · ${promo.selection.length}/3`;
        tab.addEventListener("click", () => {
          this.activePromo = index;
          this.clearMessage();
          this.render();
        });
        wrap.append(tab);

        if (this.promos.length > 1) {
          const remove = document.createElement("button");
          remove.type = "button";
          remove.className = "promo-tab-remove";
          remove.setAttribute("aria-label", `Eliminar promoción ${index + 1}`);
          remove.textContent = "×";
          remove.addEventListener("click", () => this.removePromotion(index));
          wrap.append(remove);
        }
        return wrap;
      });
      this.comboTabs.replaceChildren(...nodes);
    }

    renderGrid() {
      const filtered = this.filteredCatalog();
      this.grid.replaceChildren(...filtered.map((product) => this.createCard(product)));
      this.empty.hidden = filtered.length > 0;
      this.resultCount.textContent = `${filtered.length} referencias disponibles`;
    }

    createCard(product) {
      const selectedCount = this.currentPromo.selection.filter((item) => item.id === product.id).length;
      const button = document.createElement("button");
      button.type = "button";
      button.className = `product-card${selectedCount ? " is-selected" : ""}`;
      button.dataset.productId = product.id;
      button.setAttribute("aria-label", `Agregar ${product.name}${selectedCount ? `. Elegido ${selectedCount} ${selectedCount === 1 ? "vez" : "veces"}` : ""}`);

      const media = document.createElement("span");
      media.className = "product-card__image";
      const image = document.createElement("img");
      image.src = product.image;
      image.alt = product.name;
      image.loading = "lazy";
      image.decoding = "async";
      image.width = 640;
      image.height = 640;
      const check = document.createElement("span");
      check.className = "product-card__check";
      check.setAttribute("aria-hidden", "true");
      check.textContent = selectedCount ? `×${selectedCount}` : "+";
      media.append(image, check);

      const copy = document.createElement("span");
      copy.className = "product-card__copy";
      const category = document.createElement("span");
      category.className = "product-card__category";
      category.textContent = product.category === "novedades" ? "Novedades" : `Para ${product.category}`;
      const name = document.createElement("span");
      name.className = "product-card__name";
      name.textContent = product.name;
      copy.append(category, name);
      button.append(media, copy);
      button.addEventListener("click", () => this.addProduct(product));
      return button;
    }

    addProduct(product) {
      const selection = this.currentPromo.selection;
      this.clearMessage();
      if (selection.length >= PERFUMES_PER_PROMO) {
        this.setMessage("Esta promoción ya tiene tres perfumes. Quita uno para cambiarlo.");
        return;
      }
      selection.push(product);
      this.render();
    }

    removeProduct(index) {
      this.currentPromo.selection.splice(index, 1);
      this.clearMessage();
      this.render();
    }

    addPromotion() {
      if (this.currentPromo.selection.length !== PERFUMES_PER_PROMO) {
        this.setMessage(`Completa la promoción ${this.activePromo + 1} antes de agregar otra.`);
        return;
      }
      if (this.promos.length >= MAX_PROMOS) {
        this.setMessage(`Puedes solicitar hasta ${MAX_PROMOS} promociones en un pedido.`);
        return;
      }
      this.promos.push({ selection: [] });
      this.activePromo = this.promos.length - 1;
      this.clearMessage();
      this.render();
      this.root.querySelector("[data-toolbar]")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    removePromotion(index) {
      if (this.promos.length === 1) return;
      this.promos.splice(index, 1);
      this.activePromo = Math.min(this.activePromo, this.promos.length - 1);
      this.clearMessage();
      this.render();
    }

    formatMoney(cents) {
      return new Intl.NumberFormat("es-EC", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2
      }).format(cents / 100);
    }

    orderPricing() {
      const promoCount = this.promos.length;
      const priceEach = promoCount >= this.bulkMinimum ? this.bulkPrice : this.unitPrice;
      return { promoCount, priceEach, total: promoCount * priceEach };
    }

    renderSelection() {
      const selection = this.currentPromo.selection;
      const slotNodes = [];
      for (let index = 0; index < PERFUMES_PER_PROMO; index += 1) {
        const product = selection[index];
        const slot = document.createElement("div");
        slot.className = `selection-slot${product ? " has-product" : ""}`;
        if (product) {
          const image = document.createElement("img");
          image.src = product.image;
          image.alt = "";
          image.width = 50;
          image.height = 50;
          const name = document.createElement("strong");
          name.textContent = product.name;
          const remove = document.createElement("button");
          remove.type = "button";
          remove.className = "selection-slot__remove";
          remove.textContent = "×";
          remove.setAttribute("aria-label", `Quitar ${product.name}`);
          remove.addEventListener("click", () => this.removeProduct(index));
          slot.append(image, name, remove);
        } else {
          const number = document.createElement("span");
          number.className = "selection-slot__number";
          number.textContent = String(index + 1);
          const label = document.createElement("strong");
          label.textContent = "Elige un perfume";
          slot.append(number, label);
        }
        slotNodes.push(slot);
      }
      this.slots.replaceChildren(...slotNodes);

      const currentComplete = selection.length === PERFUMES_PER_PROMO;
      const allComplete = this.promos.every((promo) => promo.selection.length === PERFUMES_PER_PROMO);
      const configured = this.root.dataset.productConfigured === "true";
      const available = this.root.dataset.productAvailable === "true";
      const pricing = this.orderPricing();

      this.dock?.classList.toggle("has-selection", this.promos.some((promo) => promo.selection.length > 0));
      if (this.promos.some((promo) => promo.selection.length > 0)) this.dock?.classList.add("is-visible");
      this.count.textContent = `${selection.length} de 3 seleccionados`;
      this.progress.style.width = `${(selection.length / PERFUMES_PER_PROMO) * 100}%`;
      this.title.textContent = `Promoción ${this.activePromo + 1} de ${this.promos.length}`;
      if (this.selectorTitle) this.selectorTitle.textContent = `Arma la promoción ${this.activePromo + 1}`;

      this.addPromo.disabled = !currentComplete || this.promos.length >= MAX_PROMOS;
      this.addPromo.textContent = this.promos.length >= MAX_PROMOS
        ? `Máximo ${MAX_PROMOS} promociones`
        : "+ Agregar otra promoción";

      const bulkApplied = pricing.promoCount >= this.bulkMinimum;
      this.orderSummary.innerHTML = `<strong>${pricing.promoCount} ${pricing.promoCount === 1 ? "promoción" : "promociones"} · ${this.formatMoney(pricing.total)}</strong><span>${this.formatMoney(pricing.priceEach)} cada una${bulkApplied ? " · Precio especial" : ""}</span>`;

      this.submit.disabled = !allComplete || !configured || !available;
      if (!configured) {
        this.submitLabel.textContent = "Configurar producto en Shopify";
      } else if (!available) {
        this.submitLabel.textContent = "Promoción no disponible";
      } else if (!allComplete) {
        const incompleteIndex = this.promos.findIndex((promo) => promo.selection.length !== PERFUMES_PER_PROMO);
        this.submitLabel.textContent = `Completa la promoción ${incompleteIndex + 1}`;
      } else {
        this.submitLabel.textContent = `Continuar · ${this.formatMoney(pricing.total)}`;
      }
    }

    async submitOrder() {
      const allComplete = this.promos.every((promo) => promo.selection.length === PERFUMES_PER_PROMO);
      if (!allComplete || this.submit.disabled) return;
      this.submit.classList.add("is-loading");
      this.submit.disabled = true;
      this.clearMessage();

      const pricing = this.orderPricing();
      const items = this.promos.map((promo, index) => ({
        id: Number(this.root.dataset.variantId),
        quantity: 1,
        properties: {
          "Promoción": `${index + 1} de ${this.promos.length}`,
          "Perfume 1": promo.selection[0].name,
          "Perfume 2": promo.selection[1].name,
          "Perfume 3": promo.selection[2].name,
          "Precio anunciado": `${this.formatMoney(pricing.priceEach)} por promoción`,
          "_seleccion_ids": promo.selection.map((item) => item.id).join("|")
        }
      }));

      const noteLines = this.promos.map((promo, index) => [
        `PROMOCIÓN ${index + 1}`,
        `Perfume 1: ${promo.selection[0].name}`,
        `Perfume 2: ${promo.selection[1].name}`,
        `Perfume 3: ${promo.selection[2].name}`
      ].join("\n"));
      const orderNote = [
        ...noteLines,
        `TOTAL A RECAUDAR: ${this.formatMoney(pricing.total)}`,
        "PAGO: CONTRAENTREGA / CON RECAUDO",
        "ENVÍO AL CLIENTE: GRATIS"
      ].join("\n\n");

      try {
        const addResponse = await fetch(this.root.dataset.cartAddUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items })
        });
        const addData = await addResponse.json();
        if (!addResponse.ok) throw new Error(addData.description || "No se pudo agregar el pedido.");

        const noteResponse = await fetch(this.root.dataset.cartUpdateUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ note: orderNote })
        });
        if (!noteResponse.ok) throw new Error("No se pudieron guardar las notas del pedido.");

        const destination = this.root.dataset.redirect === "cart"
          ? this.root.dataset.cartUrl
          : `${window.Shopify?.routes?.root || "/"}checkout`;
        window.location.assign(destination);
      } catch (error) {
        this.setMessage(error.message || "Ocurrió un error. Intenta nuevamente.");
        this.submit.classList.remove("is-loading");
        this.submit.disabled = false;
      }
    }

    setMessage(message) {
      this.message.textContent = message;
      window.clearTimeout(this.messageTimeout);
      this.messageTimeout = window.setTimeout(() => this.clearMessage(), 6000);
    }

    clearMessage() {
      this.message.textContent = "";
    }
  }

  document.querySelectorAll("[data-promo-picker]").forEach((root) => {
    if (!root.dataset.initialized) new PromoPicker(root);
  });
})();
